import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchPosts } from "../../features/admin/PostsSlice";
import TableComponent from "../../components/TableComponent";
import ApiCustom from "../../utils/ApiCustom";
import {
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import PostModal from "../../components/admin/PostModal";
import { deletePost } from "../../features/admin/PostsSlice";
import { toast } from "react-toastify";
import {
  fetchCategoryByType,
  fetchCategoryByTypePublic,
} from "../../features/admin/CategorySlice";
import PostPreview from "../../components/admin/PostPreview";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import { fetchPartners } from "../../features/admin/PartnerSlice";
import { formatDate } from "../../utils/helps";
import { renderTextOrUnknown } from "../../utils/helpers";

const userId = localStorage.getItem("userId");
const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const hasPermission = (requiredPermission) => {
  if (userId == 1) {
    return true;
  }
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const Posts = () => {
  const dispatch = useDispatch();
  const {
    authPosts: posts,
    authPostStatus: status,
    authPostError: error,
  } = useSelector((state) => state.posts);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalPreview, setIsModalPreview] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const { categories } = useSelector((state) => state.categories);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // New state for status filtering
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchValue, setSearchValue] = useState("");

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    setIsDeleteOpen(false);
  };
  const handleOpenModal = (user = null) => {
    setCurrentUser(user);
    setIsModalOpen(true);
  };
  const handleOpenModalPreview = (user = null) => {
    setCurrentUser(user);
    setIsModalPreview(true);
  };

  const handleCancelApprove = async (post) => {
    const response = await ApiCustom.put(
      `/posts-approve/${post.id}`,
      { status: "draft" },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    if (response.status === 200) {
      dispatch(fetchPosts());
      toast.success("Huỷ duyệt bài viết thành công!");
    } else {
      toast.error("Đã xảy ra lỗi khi huỷ duyệt bài viết!");
    }
  };

  const handleCloseModalPreview = () => {
    setIsModalPreview(false);
    setCurrentUser(null);
  };

  const handleCloseModal = () => {
    dispatch(fetchPosts());
    setIsModalOpen(false);
    setCurrentUser(null);
  };

  const handleDelete = async () => {
    await dispatch(deletePost(deleteId)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        handleCloseDeleteModal();
        toast.success("Xóa bài viết thành công!");
      } else {
        toast.error("Đã xảy ra lỗi khi xóa bài viết!");
      }
    });
  };

  const handleUpdatePosition = async (id, position) => {
    try {
      const res = await ApiCustom.put(
        "/posts-position",
        { id, position },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.status === 200) {
        toast.success("Cập nhật mức độ ưu tiên thành công!");
        dispatch(fetchPosts());
      } else {
        toast.error("Đã xảy ra lỗi khi cập nhật mức độ ưu tiên!");
      }
    } catch (error) {
      toast.error("Đã xảy ra lỗi khi cập nhật mức độ ưu tiên!");
      console.error(error);
    }
  };

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchPosts());
      dispatch(fetchPartners);
    }
  }, [status, dispatch]);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (userId == 1) {
      dispatch(
        fetchCategoryByTypePublic(
          "navbar,post,training_recruitment,practice_certificate,legal_document,cadoor",
        ),
      );
    } else {
      dispatch(
        fetchCategoryByType(
          "navbar,post,training_recruitment,practice_certificate,legal_document,cadoor",
        ),
      );
    }
  }, [dispatch]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  if (status === "loading") {
    return <p>Loading...</p>;
  }

  if (status === "failed") {
    return <p>Error: {error}</p>;
  }

  const filteredPosts = posts
    .filter(
      (post) =>
        userId == 1 ||
        categories.some((category) => category.id === post.categoryId),
    )
    .filter((post) => post.type !== "notification")
    .filter((post) => statusFilter === "all" || post.status === statusFilter)
    .filter((post) =>
      searchValue === ""
        ? true
        : post.title.toLowerCase().includes(searchValue.toLowerCase()),
    );

  const headers = [
    {
      key: "id",
      label: "STT",
      render: (_, index) => {
        return <span>{index + 1}</span>;
      },
    },
    {
      key: "imageUrl",
      label: "Ảnh bìa",
      render: (item) => (
        <img
          src={`${process.env.REACT_APP_API_URL}media/${item.imageUrl}`}
          alt="Ảnh bìa"
          className="w-12 aspect-[3/2] object-cover border border-gray-300 "
        />
      ),
    },
    {
      key: "title",
      label: "Tiêu đề",
      render: (item) => (
        <div className="max-w-[500px] min-w-56 text-wrap">{item.title}</div>
      ),
    },
    {
      key: "categoryId",
      label: "Danh mục",
      render: (item) =>
        categories.find((cate) => cate.id === item.categoryId)?.name,
    },
    {
      key: "approvedAt",
      label: "Ngày duyệt",
      render: (item) =>
        renderTextOrUnknown(item?.approvedAt && formatDate(item.approvedAt)),
    },
    {
      key: "approvedBy",
      label: "Người duyệt",
      render: (item) => renderTextOrUnknown(item?.approvedByUser?.userFullName),
    },
    {
      key: "position",
      label: "Ưu tiên",
      render: (item) => {
        const currentPosition =
          item.position === null || item.position === undefined
            ? 3
            : item.position;

        return (
          <select
            className="select select-bordered select-xs"
            value={currentPosition}
            onChange={(e) =>
              handleUpdatePosition(item.id, Number(e.target.value))
            }
          >
            <option value={1}>1 - Cao</option>
            <option value={2}>2 - Trung bình</option>
            <option value={3}>3 - Thấp</option>
          </select>
        );
      },
    },
    {
      key: "status",
      label: "Trạng thái",
      render: (item) => (
        <span
          className={`px-3 py-1 rounded-full text-sm ${
            item.status === "published"
              ? "bg-green-100 text-green-800"
              : item.status === "feedback"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-red-100 text-red-800"
          } whitespace-nowrap`}
        >
          {item.status === "published"
            ? "Đã duyệt"
            : item.status === "feedback"
              ? "Đã phản hồi"
              : "Chờ duyệt"}
        </span>
      ),
    },
  ];

  if (
    hasPermission("POST_APPROVE") ||
    hasPermission("POST_UPDATE") ||
    hasPermission("POST_DELETE")
  ) {
    headers.push({ key: "setting", label: "Phương thức" });
  }

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        {hasPermission("POST_APPROVE") && (
          <button
            onClick={() => {
              item.status == "draft"
                ? handleOpenModalPreview(item)
                : handleCancelApprove(item);
            }}
            className={`btn btn-outline btn-sm px-3 my-1 rounded ${
              item.status == "published" ? "btn-error" : "btn-primary"
            }`}
            title={
              item.status == "published" ? "Huỷ duyệt" : "Xem trước, duyệt"
            }
          >
            {item.status == "published" ? (
              <XCircleIcon className="w-5 h-5" />
            ) : (
              <CheckCircleIcon className="w-5 h-5" />
            )}
          </button>
        )}
        {hasPermission("POST_UPDATE") && (
          <button
            disabled={item.status == "published"}
            onClick={() => handleOpenModal(item)}
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded "
            title="Chi tiết"
          >
            <PencilSquareIcon className="w-5 h-5" />
          </button>
        )}
        {hasPermission("POST_DELETE") && (
          <button
            onClick={() => handleOpenDeleteModal(item.id)}
            className=" btn btn-outline btn-error btn-sm px-3 my-1 rounded"
            title="Xóa"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  };
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Quản lý bài viết</h1>
      </div>

      <div className="block md:flex justify-between items-center mb-1">
        {hasPermission("POST_CREATE") ? (
          <button
            onClick={() => handleOpenModal()}
            className="btn btn-success transition-colors px-4 py-2 mb-4 rounded"
          >
            + Thêm bài viết
          </button>
        ) : (
          <div></div>
        )}
        <div className="flex space-x-4">
          <div className=" flex items-center space-x-2">
            <label
              htmlFor="userSearchFilter"
              className="text-sm font-medium w-24"
            >
              Tìm kiếm:
            </label>
            <input
              type="search"
              className="w-full px-2 py-1 border rounded-lg focus:outline-none pr-10"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
            />
          </div>
          <div className=" flex items-center space-x-2">
            <label htmlFor="userTypeFilter" className="text-sm font-medium">
              Trạng thái:
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="select select-bordered select-sm"
            >
              <option value="all">Tất cả bài viết</option>
              <option value="published">Đã duyệt</option>
              <option value="draft">Chờ duyệt</option>
            </select>
          </div>
        </div>
      </div>

      <div className="rounded-lg shadow">
        <TableComponent
          headers={headers}
          data={filteredPosts}
          settings={tableSettings}
          contentHeight="65vh"
        />
      </div>

      {isModalOpen && (
        <PostModal
          postId={currentUser?.id || null}
          onClose={handleCloseModal}
        />
      )}
      {isModalPreview && (
        <PostPreview
          postId={currentUser?.id || null}
          onClose={handleCloseModalPreview}
        />
      )}
      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Posts;